<script lang="ts" setup="">
import { computed } from 'vue'
import type { NovoIconNames } from '@novonordisk/studyhub-novoui/vue'
import { NIcon } from '@novonordisk/studyhub-novoui/vue'
import type { RouteLocationRaw } from 'vue-router'

const {
  to = undefined,
  href = undefined,
  target = undefined,
  rel = undefined,
  download = undefined,
  referrerpolicy = undefined,
  variant = 'fill',
  icon = undefined,
  flipIcon = false,
  leftIcon = undefined,
  rightIcon = undefined,
  size = 'md',
  disabled = false,
  square = false,
  rounded = true,
  loading = false,
} = defineProps<{
  to?: RouteLocationRaw
  href?: string
  target?: '_self' | '_blank' | '_parent' | '_top' | '_unfencedTop'
  rel?: string
  download?: string
  referrerpolicy?: string
  variant?: 'fill' | 'outline' | 'text' | 'outlineSubtle' | 'destructive' | 'destructiveOutlineSubtle' | 'teams'
  icon?: NovoIconNames
  flipIcon?: boolean
  leftIcon?: NovoIconNames
  rightIcon?: NovoIconNames
  size?: 'xs' | 'tiny' | 'small' | 'sm' | 'md' | 'medium' | 'lg' | 'large'
  disabled?: boolean
  square?: boolean
  rounded?: boolean
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'click', value: Event): void
}>()

type ComponentType = 'RouterLink' | 'a' | 'button'

const componentType = computed((): ComponentType => {
  if (to) {
    return 'RouterLink'
  }

  if (href) {
    return 'a'
  }

  return 'button'
})

const componentTypeProps = computed(() => {
  if (componentType.value === 'RouterLink') {
    return {
      to: to,
    }
  }

  if (componentType.value === 'a') {
    return {
      href: href,
      target: target,
      rel: rel,
      download: download,
      referrerpolicy: referrerpolicy,
    }
  }

  return {
    disabled: disabled,
  }
})

const iconSize = computed(() => {
  if (icon) {
    if (['xs', 'tiny', 'sm', 'small'].includes(size)) {
      return 20
    }

    return 24
  } else {
    if (['xs', 'tiny'].includes(size)) {
      return 14
    }

    if (['sm', 'small'].includes(size)) {
      return 14
    }

    return 16
  }
})

const events = computed(() => {
  if (loading) {
    return {}
  }

  return {
    click: (e: Event) => {
      emit('click', e)
    },
  }
})
</script>

<template>
  <component
    :is="componentType"
    :class="[
      {
        'border-general-high-emphasis-enabled bg-general-high-emphasis-enabled text-general-inverted ring-general-focus hover:border-general-high-emphasis-hover hover:bg-general-high-emphasis-hover active:border-general-high-emphasis-active active:bg-general-high-emphasis-active':
          !disabled && variant === 'fill',
        'text-general-inverted ring-general-focus border-[#7B83EB] bg-[#7B83EB] hover:border-[#505AC9] hover:bg-[#505AC9] active:border-[#464EB8] active:bg-[#464EB8]':
          !disabled && variant === 'teams',
        'border-alert-enabled bg-alert-high-emphasis-enabled text-general-inverted ring-button-destructive-focus hover:bg-alert-high-emphasis-hover active:bg-alert-high-emphasis-active':
          !disabled && variant === 'destructive',
        'text-general-high-emphasis-enabled hover:bg-general-medium-emphasis-hover active:bg-general-medium-emphasis-active border-transparent bg-transparent ring-[#C2D8F4]':
          !disabled && variant === 'text',
        'border-general-high-emphasis-enabled text-general-high-emphasis-enabled ring-general-focus hover:border-general-high-emphasis-hover hover:bg-general-medium-emphasis-hover hover:text-general-high-emphasis-hover active:border-general-high-emphasis-active active:bg-general-medium-emphasis-active active:text-general-high-emphasis-active bg-transparent':
          !disabled && variant === 'outline',
        'border-general-medium bg-general-base text-general-primary ring-general-focus hover:border-general-high-emphasis-enabled hover:bg-general-medium-emphasis-hover active:border-general-high-emphasis-active active:bg-general-medium-emphasis-active':
          !disabled && variant === 'outlineSubtle',
        'border-general-medium bg-general-base text-alert-enabled ring-general-focus hover:border-alert-enabled hover:bg-alert-low-emphasis-hover active:border-alert-active active:bg-[#ef9486]':
          !disabled && variant === 'destructiveOutlineSubtle',
      },
      {
        'border-disabled-subtle bg-disabled-strong text-disabled-medium':
          (disabled && variant === 'fill') ||
          (disabled && variant === 'destructive') ||
          (disabled && variant === 'teams'),
        'text-disabled-medium border-transparent bg-transparent': disabled && variant === 'text',
        'border-disabled-subtle text-disabled-medium bg-transparent':
          (disabled && variant === 'outline') || (disabled && variant === 'outlineSubtle'),
      },
      {
        'text-ui-button-12 h-8 px-4 leading-[32px] font-medium': !icon && ['xs', 'tiny'].includes(size),
        'text-ui-button-14 h-10 px-6 leading-[40px] font-medium': !icon && ['sm', 'small'].includes(size),
        'text-ui-button-14 h-12 px-6 leading-[48px] font-medium': !icon && ['md', 'medium'].includes(size),
        'text-ui-button-16 h-[54px] px-6 leading-[54px] font-medium': !icon && ['lg', 'large'].includes(size),
      },
      {
        'flex size-8 items-center justify-center': !!icon && ['xs', 'tiny'].includes(size),
        'flex size-10 items-center justify-center': !!icon && ['sm', 'small'].includes(size),
        'flex size-12 items-center justify-center': !!icon && ['md', 'medium'].includes(size),
        'flex size-[54px] items-center justify-center': !!icon && ['lg', 'large'].includes(size),
      },
      {
        'rounded-x-large': !square && !rounded,
        rounded: rounded,
        'rounded-none': square,
      },
      {
        'pointer-events-none': disabled,
      },
    ]"
    class="group font-500 relative inline-block border whitespace-nowrap outline-0 transition-colors duration-100 ease-in-out"
    v-bind="componentTypeProps"
    v-on="events"
  >
    <VLoader
      v-if="loading"
      :stroke-class="[
        {
          'stroke-general-inverted': !disabled && ['fill', 'teams', 'destructive'].includes(variant),
          'stroke-general-primary': !disabled && ['text', 'outline', 'outlineSubtle'].includes(variant),
          'stroke-alert-enabled': !disabled && ['destructiveOutlineSubtle'].includes(variant),
          'stroke-disabled-medium': disabled,
        },
      ]"
      class="absolute! top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      :width="['sm', 'small'].includes(size) ? '1.2em' : ['xs', 'tiny'].includes(size) ? '0.8em' : '1.5em'"
    />
    <span
      :class="[
        {
          'opacity-0': loading,
        },
      ]"
      class="flex w-full items-center justify-center gap-[0.7em]"
    >
      <template v-if="icon">
        <NIcon
          :class="[
            {
              'fill-general-inverted':
                !disabled && (variant === 'fill' || variant === 'destructive' || variant === 'teams'),
              'fill-general-primary': !disabled && variant === 'outlineSubtle',
              'fill-general-high-emphasis-enabled': !disabled && variant === 'text',
              'fill-general-high-emphasis-enabled group-hover:fill-general-high-emphasis-hover group-active:fill-general-high-emphasis-active':
                !disabled && variant === 'outline',
              'fill-alert-enabled': !disabled && variant === 'destructiveOutlineSubtle',
              'fill-disabled-medium': disabled,
              '-scale-x-100': flipIcon,
            },
          ]"
          :icon="icon"
          :size="iconSize"
        />
      </template>
      <template v-else>
        <img
          v-if="variant === 'teams'"
          src="/assets/icons/microsoftteams.svg"
          alt=""
          :style="{ width: `${iconSize}px`, height: `${iconSize}px` }"
        />
        <NIcon
          v-else-if="leftIcon"
          :class="[
            {
              'fill-general-inverted': !disabled && (variant === 'fill' || variant === 'destructive'),
              'fill-general-high-emphasis-enabled': !disabled && variant === 'text',
              'fill-general-high-emphasis-enabled group-hover:fill-general-high-emphasis-hover group-active:fill-general-high-emphasis-active':
                !disabled && variant === 'outline',
              'fill-general-primary': !disabled && variant === 'outlineSubtle',
              'fill-alert-enabled': !disabled && variant === 'destructiveOutlineSubtle',
            },
            {
              'fill-disabled-medium': disabled,
            },
          ]"
          :icon="leftIcon"
          :size="iconSize"
        />
        <slot />
        <NIcon
          v-if="rightIcon"
          :class="[
            {
              'fill-general-inverted':
                !disabled && (variant === 'fill' || variant === 'destructive' || variant === 'teams'),
              'fill-general-high-emphasis-enabled': !disabled && variant === 'text',
              'fill-general-high-emphasis-enabled group-hover:fill-general-high-emphasis-hover group-active:fill-general-high-emphasis-active':
                !disabled && variant === 'outline',
              'fill-general-primary': !disabled && variant === 'outlineSubtle',
              'fill-alert-enabled': !disabled && variant === 'destructiveOutlineSubtle',
            },
            {
              'fill-disabled-medium': disabled,
            },
          ]"
          :icon="rightIcon"
          :size="iconSize"
        />
      </template>
    </span>
  </component>
</template>
